import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { DateToLocal } from "components/dates";
import { Filter, FilterButton } from "components/filtering";
import { FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { StatusBlock } from "components/status";
import { Tooltip } from "components/tooltip";
import { useDebounce } from "hooks/useDebounce";
import { useUserHasAccess } from "hooks/useUserHasAccess";
import { NewReviewModal } from "pages/employee-reviews/new-review-modal";
import { awaitingSignOffStatusBand, completedStatusBand, noPerformReviewAccessMessage, pendingStatusBands, RaisedByDisplay } from "pages/employee-reviews/shared";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { EmployeeReviewsService } from "services";

const DEBOUNCE_DELAY_MS = 750;
const PAGE_SIZE = 25;
const performAction = "EmployeeReview:Perform";

// The combined list holds pending and completed rows, so it carries the pending urgency bands
// plus the states a completed review can be in. Declared in the order the API's status enum
// gives them, which is the order the status filter offers them in.
const statusBands = {
    ...pendingStatusBands,
    AwaitingSignOff: awaitingSignOffStatusBand,
    Completed: completedStatusBand
};

const statusFilterItems = Object.entries(statusBands).map(([id, band]) => ({ id: id, name: band.label }));

const statusFilter = {
    name: "Status",
    displayName: "Status",
    items: statusFilterItems
};

const tableHeaders = ["Review Type", "Due Date", "Completed", "Status", "Raised By", "Outcome Level"];
const sortableHeaders = [
    { headerName: "Review Type", sortName: "reviewtype" },
    { headerName: "Due Date", sortName: "duedate" },
    { headerName: "Completed", sortName: "completeddate" },
    { headerName: "Status", sortName: "status" },
    { headerName: "Raised By", sortName: "raisedby" },
    { headerName: "Outcome Level", sortName: "outcomelevel" }
];

// The combined list defaults to most-recent-activity first (server-side); this isn't one of the
// visible columns, so no header shows a sort caret until the user picks one.
const defaultSortHeader = { headerName: "Recent Activity", sortName: "recentactivity" };

function renderStatusBand(status) {
    const band = statusBands[status];

    if (band == null) {
        return status;
    }

    // Status values are shown bold per the design.
    return <strong className={band.className}>{band.label}</strong>;
}

// The review type is the row's identifying column, so it carries the link to the review itself.
// The response row carries rowType plus the id that applies: a completed row opens the
// completed-review view, and a pending row opens the complete-review wizard. Both views are owned
// by the standalone reviews hub, so the links target its routes. The wizard needs the Perform
// action, so a user without it sees a pending row's review type as plain text, with a tooltip
// giving the reason, instead of a link to a page they cannot use.
function renderReviewType(item, rowKey, canPerform) {
    let reviewUrl;

    if (item.rowType === "Completed") {
        reviewUrl = `/employee-reviews/completed-reviews/${item.reviewId}`;
    } else if (canPerform) {
        reviewUrl = `/employee-reviews/pending-reviews/${item.pendingReviewId}/complete`;
    } else {
        // The tooltip renders no element of its own, so the review type needs a wrapping span to
        // give it a hoverable target.
        return (
            <Tooltip key={`review-type-${rowKey}`} content={noPerformReviewAccessMessage}>
                <span>{item.reviewType}</span>
            </Tooltip>
        );
    }

    return <Link key={`review-type-${rowKey}`} to={reviewUrl}>{item.reviewType}</Link>;
}

export function EmployeeReviews({ employeeId }) {
    const userHasAccess = useUserHasAccess();
    const canPerform = userHasAccess(performAction);

    const [loadingData, setLoadingData] = useState(true);
    const [reviewItems, setReviewItems] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [sortedBy, setSortedBy] = useState(defaultSortHeader);
    const [sortedDir, setSortedDir] = useState("D");
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY_MS);
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([statusFilter]);
    const [outcomeLevels, setOutcomeLevels] = useState([]);
    const [reviewTypes, setReviewTypes] = useState(null);
    const [reviewTypesFailed, setReviewTypesFailed] = useState(false);
    const [newReviewModalOpen, setNewReviewModalOpen] = useState(false);

    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSortedBy = useRef();
    const currentSortedDir = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();

    // Options give the outcome-level colours that the listing's outcome-level column renders with,
    // and the raised-by values the Raised By filter offers.
    useEffect(() => {
        EmployeeReviewsService.getOptions().then(
            (options) => {
                setOutcomeLevels(options?.outcomeLevels ?? []);
                setPageFilters([
                    statusFilter,
                    {
                        name: "RaisedBy",
                        displayName: "Raised By",
                        items: (options?.raisedBy ?? []).map((raisedBy) => ({ id: raisedBy.id.toString(), name: raisedBy.name }))
                    }
                ]);
            },
            () => {
                setOutcomeLevels([]);
                NotificationToaster.show(Intent.DANGER, "Could not load review options. Please try again.");
            }
        );
    }, []);

    // The review types a review can be raised against are the ones that apply to this driver, so
    // they come from the driver's own endpoint rather than from the account-wide options. Only a
    // user who can raise one needs them.
    useEffect(() => {
        if (!canPerform) {
            return;
        }

        EmployeeReviewsService.getAvailableReviewTypes(employeeId).then(
            (availableReviewTypes) => {
                setReviewTypes(availableReviewTypes ?? []);
            },
            () => {
                setReviewTypesFailed(true);
                NotificationToaster.show(Intent.DANGER, "Could not load the review types for this employee. Please try again.");
            }
        );
    }, [employeeId, canPerform]);

    // Outcome level renders as a coloured block, matching the review-types page and its level
    // colour scheme (the colour comes from the options' outcome levels).
    function getLevelColour(levelNumber) {
        const level = outcomeLevels.find((option) => option.number === levelNumber);

        // StatusBlock expects the hex value without the leading hash.
        return level?.colour == null ? "" : level.colour.replace("#", "");
    }

    function renderOutcomeLevel(outcomeLevel) {
        if (outcomeLevel == null) {
            return (<span>-</span>);
        }

        return (
            <span title={outcomeLevel.name}>
                <StatusBlock large={false} bold={true} background={getLevelColour(outcomeLevel.number)}>{`Level ${outcomeLevel.number}`}</StatusBlock>
            </span>
        );
    }

    function fetchData() {
        if (
            currentPageNumber.current === pageNumber &&
            currentPageSize.current === pageSize &&
            currentSortedBy.current === sortedBy &&
            currentSortedDir.current === sortedDir &&
            currentSearchTerm.current === debouncedSearchTerm &&
            currentAppliedFilters.current === appliedFilters
        ) {
            return;
        }

        currentPageNumber.current = pageNumber;
        currentPageSize.current = pageSize;
        currentSortedBy.current = sortedBy;
        currentSortedDir.current = sortedDir;
        currentSearchTerm.current = debouncedSearchTerm;
        currentAppliedFilters.current = appliedFilters;

        setLoadingData(true);

        const request = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            searchTerm: debouncedSearchTerm,
            filters: appliedFilters.map((f) => ({ key: f.name, value: f.value }))
        };

        EmployeeReviewsService.searchEmployeeReviews(employeeId, request).then(
            (response) => {
                setReviewItems(response?.data ?? []);
                setTotalRecords(response?.totalCount ?? 0);
                setLoadingData(false);
            },
            () => {
                NotificationToaster.show(Intent.DANGER, "Could not load reviews. Please try again.");
                setLoadingData(false);
            }
        );
    }

    useEffect(() => {
        fetchData();
    }, [pageNumber, pageSize, sortedBy, sortedDir, debouncedSearchTerm, appliedFilters]);

    function onSearchChange(e) {
        setSearchTerm(e.target.value);
        setPageNumber(1);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
        setPageNumber(1);
    }

    function onSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function renderNewReview() {
        // The types could not be loaded, so no review can be started against a type we cannot
        // confirm applies to this driver.
        if (reviewTypesFailed) {
            return (
                <Tooltip content="The review types could not be loaded. Refresh the page to try again.">
                    {/* A disabled <button> swallows hover events, so the span is the tooltip's target. */}
                    <span>
                        <Button id="new-review" text="New Review" icon="plus" intent="primary" disabled onClick={() => { }} />
                    </span>
                </Tooltip>
            );
        }

        if (reviewTypes === null) {
            return <Button id="new-review" text="New Review" icon="plus" intent="primary" loading onClick={() => { }} />;
        }

        // No type applies to this driver, so there is nothing to raise — explain why the button is
        // disabled rather than leaving the user guessing.
        if (reviewTypes.length === 0) {
            return (
                <Tooltip content="There are no review types available for this employee.">
                    {/* A disabled <button> swallows hover events, so the span is the tooltip's target. */}
                    <span>
                        <Button id="new-review" text="New Review" icon="plus" intent="primary" disabled onClick={() => { }} />
                    </span>
                </Tooltip>
            );
        }

        return <Button id="new-review" text="New Review" icon="plus" intent="primary" onClick={() => setNewReviewModalOpen(true)} />;
    }

    const tableRows = reviewItems.map((item) => {
        const rowKey = item.rowType === "Completed" ? `completed-${item.reviewId}` : `pending-${item.pendingReviewId}`;

        return [
            renderReviewType(item, rowKey, canPerform),
            <DateToLocal key={`due-${rowKey}`} format="DD/MM/YYYY">{item.dueDate}</DateToLocal>,
            item.completedDate
                ? <DateToLocal key={`completed-date-${rowKey}`} format="DD/MM/YYYY">{item.completedDate}</DateToLocal>
                : "-",
            renderStatusBand(item.status),
            <RaisedByDisplay key={`raised-by-${rowKey}`} raisedBy={item.raisedBy} triggers={item.triggers} />,
            renderOutcomeLevel(item.outcomeLevel)
        ];
    });

    return (
        <div className="row">
            <div className="inline-items inline-items-space spacer-bottom">
                <h2>Reviews</h2>
                <ShowHide evaluator={canPerform} show={renderNewReview()} />
            </div>

            <div className="spacer-bottom-small">
                <div className="filter-controls">
                    <div className="inline-items">
                        <div className="inline-item fill">
                            <div className="form-field-full">
                                <FormTextInput
                                    placeholder="Search review types"
                                    onChange={onSearchChange}
                                    value={searchTerm}
                                    large
                                    disabled={loadingData}
                                    icon="search"
                                    id="employee-reviews-search-field"
                                />
                            </div>
                        </div>
                        <div className="inline-item">
                            <FilterButton onToggle={() => setShowFilters((p) => !p)} filters={appliedFilters} />
                        </div>
                    </div>
                    <Filter
                        filterName="employeeReviews"
                        filters={pageFilters}
                        visible={showFilters}
                        numOfColumns={1}
                        onUpdate={onFilterChange}
                        onClickOutside={() => setShowFilters((p) => !p)}
                    />
                </div>
            </div>

            <ListingTable
                id="employee-reviews-listing"
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={loadingData}
                noDataMessage="No reviews found for this employee."
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onSort}
                pageSize={pageSize}
                onPagingChange={onPagingChange}
            />

            {/* The employee is already known here, so the modal is left with only the review type to pick. */}
            <NewReviewModal isOpen={newReviewModalOpen} employeeId={employeeId} onClose={() => setNewReviewModalOpen(false)} />
        </div>
    );
}

EmployeeReviews.propTypes = {
    employeeId: PropTypes.string.isRequired
};
